/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AppError } from "@tivocorp/data-domain-lib";

import { BaseErrorMapper } from "../../model/errorMapper/BaseErrorMapper";
import { Locale, LocaleKeys } from "../../model/locale";

/**
 * Catalog error codes for Guide operational events (UAN-3879, "Guide
 * Operational and analytical logging" §5). Codes come only from
 * tivocorp/unity-client-error-codes. A701/A702/A750 declare no `subcodes`
 * in the catalog; the sub-error suffix is appended at report time by
 * {@link GuideErrorMapper} from the shared ErrorCodeUtil. Scenarios told
 * apart by message share a code.
 */
export const GUIDE_ERROR_CODE = {
  /** A702 "Connected Query Failure" — channel list fetch failed / empty. */
  CHANNELS_LOAD_FAILED: "A702",
  /** A701 "Service Connection Recoverable Error / Fail Open State" — EPG rows. */
  ROWS_LOAD_FAILED: "A701",
  /** A701 — program preview (guide data) fetch failed. */
  PREVIEW_FETCH_FAILED: "A701",
  /** A701 — guide menu items (filter list) fetch failed. */
  FILTERS_FETCH_FAILED: "A701",
  /** A701 — filter channel ids fetch failed. */
  FILTER_CHANNEL_IDS_FETCH_FAILED: "A701",
  /** A750 "Whisper Favorite Channels Update Error". */
  FAVORITE_TOGGLE_FAILED: "A750"
} as const;

/** screen_name for Guide analytics events and error_context.screen_name for uiError. */
export const GUIDE_SCREEN_NAME = "Guide Grid";

/**
 * Whether a guide request rejection is a cancellation rather than a failure.
 *
 * A superseded request is expected and must be dropped silently — neither
 * logged nor recovered from. Covers both transports in use: axios/Sandwich
 * (`CanceledError` / `ERR_CANCELED`, or the legacy `Cancel` object flagged
 * `__CANCEL__`, as classified in
 * GridGuideServiceRepositoryImpl) and the DOM/fetch `AbortError`.
 */
export const isCancellation = (error: unknown): boolean => {
  if (error === null || error === undefined) {
    return false;
  }
  const candidate = error as {
    name?: unknown;
    code?: unknown;
    __CANCEL__?: unknown;
  };
  return (
    // Legacy axios `Cancel` (no name/code) — what `axios.isCancel` checks.
    candidate.__CANCEL__ === true ||
    candidate.name === "CanceledError" ||
    candidate.name === "AbortError" ||
    candidate.code === "ERR_CANCELED"
  );
};

export type GuideErrorCode =
  (typeof GUIDE_ERROR_CODE)[keyof typeof GUIDE_ERROR_CODE];

/** Localised dialog title. Read per call — locale files load asynchronously. */
export const getGuideErrorTitle = (): string =>
  Locale.getString(LocaleKeys.HOME_SCREEN_ERROR_TITLE);

/** Localised dialog body. See {@link getGuideErrorTitle}. */
export const getGuideErrorDescription = (): string =>
  Locale.getString(LocaleKeys.GUIDE_CHANNEL_SERVICE_UNAVAILABLE_MESSAGE);

/**
 * Error mapper for the Guide screen, mirroring HomeScreenErrorMapper.
 *
 * The sub-error suffix (N6xx transport, S6xx service context, G4xx-G6xx HTTP
 * status) comes from the shared ErrorCodeUtil, so the same failure reports
 * the same sub-code on every screen. It is left empty — bare `A701` — when no
 * layer recognises the failure; a sub-code is never guessed.
 */
export class GuideErrorMapper extends BaseErrorMapper {
  /**
   * Error message to report: the Guide scenario `message` with the raw error's
   * own text appended (`"Failed to load guide rows: Network Error"`), or just
   * `message` when the error carries none.
   */
  public static toErrorMessage(error: unknown, message: string): string {
    const detail: string = this.extractMessage(error);
    return detail.length > 0 ? `${message}: ${detail}` : message;
  }

  /**
   * Maps a raw error from a Guide call to an AppError for the given code.
   *
   * @param code - The Guide catalog code ({@link GUIDE_ERROR_CODE})
   * @param error - The raw error thrown by the service, cache or interactor
   * @param message - Guide scenario message; the error's own text is appended
   */
  public static fromError(
    code: GuideErrorCode,
    error: unknown,
    message: string
  ): AppError {
    return this.buildAppError(
      code,
      this.toErrorMessage(error, message),
      this.extractSubErrorCode(error),
      getGuideErrorTitle(),
      getGuideErrorDescription()
    );
  }

  /**
   * Composite code to report: `"A701-G500"`, or plain `"A701"` when there is
   * no sub-error code.
   */
  public static toCompositeCode(code: GuideErrorCode, error: unknown): string {
    return this.fromError(code, error, "").getUIErrorwithSubErrorCode();
  }
}
